import {
    createContext,
    useCallback,
    useContext,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { BoardPlacement } from "./Board.types";
import { getCellKey } from "./boardCells";
import {
    captureScrollSnapshot,
    readScrollSnapshot,
    type BoardListArgs,
} from "./boardScrollSnapshot";
import { createPinnedRangeExtractor, getWindowRuns } from "./boardWindowing";
import { useListStart } from "./useListStart";

/** The element board lists scroll in: their column, or the board with lanes. */
export interface BoardScroller {
    element: HTMLElement | null;
    /** The board's scroller with lanes, which every lane and cell list shares. */
    isShared: boolean;
}

export const BoardScrollerContext = createContext<BoardScroller>({
    element: null,
    isShared: false,
});

/**
 * Set by windowed lanes: whether a lane is in their window. A cell's list
 * leaving with its lane isn't being replaced, so it leaves no snapshot.
 */
export const BoardLaneWindowContext = createContext<
    ((laneId: string) => boolean) | null
>(null);

interface UseBoardWindowArgs {
    /** The cell the list renders; null for the lane list. */
    placement: BoardPlacement | null;
    count: number;
    getItemKey: (index: number) => string;
    estimateSize: (index: number) => number;
    gap: number;
    overscan: number;
    /** Window edges snap to multiples of this. */
    chunk: number;
    /** Indexes that stay mounted wherever they are. */
    pinnedIndexes: readonly number[];
    /**
     * Whether a measured item above the viewport shifts the scroll by its
     * size change. Defaults to the virtualizer's own rule.
     */
    shouldAdjustScrollPositionOnItemSizeChange?: () => boolean;
}

const NO_SIZES: ReadonlyMap<string, number> = new Map();

/**
 * Windows a list that scrolls in the nearest `BoardScrollerContext`. Items
 * are measured as they mount; `runs` lists the mounted items with spacers
 * for the rest, so the list stays in normal flow. A list replacing a plain
 * one (crossing the windowing threshold) starts from the sizes that one had
 * rendered, so nothing above the viewport moves.
 */
export const useBoardWindow = ({
    placement,
    count,
    getItemKey,
    estimateSize,
    gap,
    overscan,
    chunk,
    pinnedIndexes,
    shouldAdjustScrollPositionOnItemSizeChange,
}: UseBoardWindowArgs) => {
    const { element: scroller } = useContext(BoardScrollerContext);
    const [list, setList] = useState<HTMLElement | null>(null);
    const isLaneInWindow = useContext(BoardLaneWindowContext);
    const cellKey = placement === null ? null : getCellKey(placement);
    const laneId = placement?.laneId ?? null;
    const listStart = useListStart(list, scroller);
    const rangeExtractor = useMemo(
        () => createPinnedRangeExtractor({ pinnedIndexes, chunk }),
        [pinnedIndexes, chunk],
    );
    const seededSizesRef = useRef(NO_SIZES);
    const estimateSeededSize = useCallback(
        (index: number) =>
            seededSizesRef.current.get(getItemKey(index)) ??
            estimateSize(index),
        [getItemKey, estimateSize],
    );
    // oxlint-disable-next-line react/incompatible-library -- intended skip: callers only get fresh `runs` and stable live-reading methods
    const virtualizer = useVirtualizer({
        count,
        getScrollElement: () => scroller,
        // A list mounting in an already scrolled board (a lane coming into
        // view) would otherwise scroll it back to the top as it attaches.
        initialOffset: () => scroller?.scrollTop ?? 0,
        estimateSize: estimateSeededSize,
        getItemKey,
        gap,
        overscan,
        scrollMargin: listStart,
        rangeExtractor,
        // Lanes share one scroller, so many lists update per scroll event;
        // batching them into one render beats a synchronous render each.
        useFlushSync: false,
    });
    // Not a `useVirtualizer` option in this version, only an instance field.
    // A new list mounts no items until its scroller is measured after the
    // first commit, so this is set before anything is measured.
    useLayoutEffect(() => {
        virtualizer.shouldAdjustScrollPositionOnItemSizeChange =
            shouldAdjustScrollPositionOnItemSizeChange;
    }, [virtualizer, shouldAdjustScrollPositionOnItemSizeChange]);

    // Declared before the seeding below so it runs a commit later, once the
    // seeded sizes have rendered: the estimated first render can be shorter
    // than the plain list was, and the browser clamps the scroll to it.
    const restoreScrollTopRef = useRef<number | null>(null);
    useLayoutEffect(() => {
        const scrollTop = restoreScrollTopRef.current;
        if (scrollTop !== null && scroller !== null) {
            restoreScrollTopRef.current = null;
            scroller.scrollTop = scrollTop;
        }
    });
    useLayoutEffect(() => {
        if (scroller === null) {
            return undefined;
        }
        const swap: BoardListArgs = { scroller, kind: "windowed", cellKey };
        const snapshot = readScrollSnapshot(swap);
        if (snapshot !== undefined) {
            seededSizesRef.current = snapshot.sizes;
            restoreScrollTopRef.current = snapshot.scrollTop;
            virtualizer.measure();
        }
        return () => {
            if (laneId === null || isLaneInWindow?.(laneId) !== false) {
                captureScrollSnapshot(swap);
            }
        };
    }, [scroller, virtualizer, cellKey, laneId, isLaneInWindow]);

    const runs = getWindowRuns({
        items: virtualizer.getVirtualItems(),
        count,
        gap,
        listStart,
        listEnd: listStart + virtualizer.getTotalSize(),
    });

    const isKeyMounted = useCallback(
        (key: string) =>
            virtualizer.getVirtualItems().some((item) => item.key === key),
        [virtualizer],
    );

    return {
        listRef: setList,
        runs,
        isKeyMounted,
        measureElement: virtualizer.measureElement,
        scrollToIndex: virtualizer.scrollToIndex,
    };
};
